import { fail, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { readFormats, regelnVomTyp } from "@/office/excel/formate";
import { bezuege } from "@/office/excel/formeln";
import { nichtPruefbar } from "../schnittstelle";

export const lektionVisuell: Lesson = {
  id: "visuell",
  title: "Datenbalken, Farbskalen und Symbole",
  summary: "Ein Diagramm in der Zelle – und die Grenze, ab der Farbe zur Behauptung wird.",
  durationMinutes: 11,
  learningGoals: [
    "Datenbalken, Farbskalen und Symbolsätze auseinanderhalten",
    "Eine Regel über eine Formel auf die ganze Zeile anwenden",
    "Farbe barrierefrei einsetzen",
  ],
  steps: [
    {
      id: "drei-arten",
      title: "Drei Arten, Größenverhältnisse zu zeigen",
      explanation: [
        "Neben den Ja-Nein-Regeln gibt es drei Darstellungen, die den Wert selbst " +
          "abbilden statt nur eine Schwelle zu prüfen.",
        "Datenbalken zeichnen einen Balken in die Zelle, dessen Länge dem Wert " +
          "entspricht. Das ist ein Säulendiagramm ohne Diagramm – man sieht " +
          "Größenverhältnisse auf einen Blick und behält gleichzeitig die genaue Zahl. " +
          "Für Zahlenspalten ist das oft die nützlichste aller bedingten " +
          "Formatierungen.",
        "Farbskalen färben von einem Farbton zum anderen durch, meist Rot über Gelb " +
          "nach Grün. Sie eignen sich für Rasterdaten – etwa eine Kreuztabelle Monat " +
          "mal Region –, wo man Muster sucht statt einzelne Werte. In einer schmalen " +
          "Zahlenspalte sind sie schwächer als Datenbalken, weil sich Farbtöne " +
          "schlechter vergleichen lassen als Längen.",
        "Symbolsätze setzen Ampeln, Pfeile oder Sterne in die Zelle. Sie sind die " +
          "grobkörnigste Variante und teilen die Werte in drei bis fünf Klassen ein. " +
          "Ihr Nutzen steht und fällt mit den Schwellen: In der Voreinstellung teilt " +
          "Excel nach Prozenträngen ein, was fast nie gemeint ist. Wer Ampeln " +
          "einsetzt, sollte die Schwellen im Regeldialog auf feste Werte umstellen.",
      ],
      keyPoints: [
        "Datenbalken für Zahlenspalten – Längen vergleichen sich am besten.",
        "Farbskalen für Raster, in denen man Muster sucht.",
        "Symbolsätze: die voreingestellten Prozentränge fast immer auf Werte umstellen.",
      ],
      task: {
        instruction:
          "Ergänze eine Datenbalken-, Farbskalen- oder Symbolregel für eine deiner " +
          "Zahlenspalten.",
        hint: "Spalte markieren, dann Start → Bedingte Formatierung → Datenbalken.",
        check: async () => {
          const formate = await readFormats();
          if (formate.conditionalFormats === null) {
            return nichtPruefbar(
              "Die bedingten Formatierungen eines Bereichs",
              "1.6",
              "Sieh nach, ob in deiner Zahlenspalte Balken, Farbverläufe oder Symbole in den Zellen stehen.",
              true,
            );
          }

          const visuell = [
            ...regelnVomTyp(formate, "DataBar"),
            ...regelnVomTyp(formate, "ColorScale"),
            ...regelnVomTyp(formate, "IconSet"),
          ];

          if (visuell.length === 0) {
            const vorhandene = formate.conditionalFormats;
            return fail(
              vorhandene.length === 0
                ? "In der Arbeitsmappe ist noch keine bedingte Formatierung hinterlegt."
                : `Es gibt ${vorhandene.length} ${vorhandene.length === 1 ? "Regel" : "Regeln"} ` +
                  `(${[...new Set(vorhandene.map((regel) => regel.type))].join(", ")}), aber keine ` +
                  "Datenbalken, Farbskala oder Symbolregel.",
              "Markiere eine Zahlenspalte und wähle Start → Bedingte Formatierung → Datenbalken.",
            );
          }

          const erste = visuell[0];
          const benennung: Record<string, string> = {
            DataBar: "Datenbalken",
            ColorScale: "Farbskala",
            IconSet: "Symbolsatz",
          };
          return pass(
            `${benennung[erste.type] ?? erste.type} auf „${erste.sheetName}“ über ` +
              `${erste.address ?? "einen Bereich"} – die Größenverhältnisse sind jetzt ohne Lesen sichtbar.`,
          );
        },
      },
    },
    {
      id: "ganze-zeile",
      title: "Die ganze Zeile einfärben",
      explanation: [
        "Die fertigen Regeln färben immer nur die Zelle, in der der Wert steht. " +
          "Meistens will man aber die ganze Zeile hervorheben: Alle Bestellungen über " +
          "1000 Euro sollen komplett markiert sein, nicht nur die Betragszelle.",
        "Dafür gibt es „Neue Regel“ → „Formel zur Ermittlung der zu formatierenden " +
          "Zellen verwenden“. Die Formel wird für die linke obere Zelle des " +
          "Geltungsbereichs geschrieben und von Excel auf alle übrigen übertragen – " +
          "genau wie beim Ausfüllen einer Formel.",
        "Und damit ist klar, welche Dollarzeichen hingehören: Die Spalte, in der die " +
          "Bedingung geprüft wird, muss festgehalten werden, die Zeile nicht. " +
          "=$D2>1000 heißt: Prüfe immer Spalte D, aber in der jeweiligen Zeile. Das ist " +
          "ein gemischter Bezug – genau der Fall, für den es sie gibt.",
        "Die häufigsten Fehler dabei sind zwei. Erstens ein vollständig absoluter " +
          "Bezug =$D$2>1000: Dann prüfen alle Zellen dieselbe Zelle, und entweder die " +
          "ganze Tabelle ist gefärbt oder gar nichts. Zweitens ein Geltungsbereich, " +
          "der nur eine Spalte umfasst – dann stimmt die Formel, aber gefärbt wird " +
          "trotzdem nur diese eine Spalte.",
      ],
      keyPoints: [
        "Neue Regel → „Formel zur Ermittlung der zu formatierenden Zellen verwenden“.",
        "=$D2>1000 – Spalte festhalten, Zeile mitlaufen lassen.",
        "Der Geltungsbereich muss alle Spalten der Zeile umfassen.",
      ],
      task: {
        instruction:
          "Lege eine Regel mit eigener Formel an, die eine ganze Zeile einfärbt, wenn " +
          "eine Bedingung zutrifft.",
        hint: "Bereich über alle Spalten markieren, dann Neue Regel → Formel verwenden → =$D2>1000 mit angepasster Spalte.",
        check: async () => {
          const formate = await readFormats();
          if (formate.conditionalFormats === null) {
            return nichtPruefbar(
              "Die bedingten Formatierungen eines Bereichs",
              "1.6",
              "Öffne „Regeln verwalten“ und sieh nach, ob dort eine Regel vom Typ „Formel“ steht.",
              true,
            );
          }

          const eigene = regelnVomTyp(formate, "Custom");
          if (eigene.length === 0) {
            return fail(
              "Es gibt noch keine Regel, die eine eigene Formel benutzt.",
              "Markiere den Bereich über alle Spalten deiner Liste, dann Start → Bedingte Formatierung → " +
                "Neue Regel → „Formel zur Ermittlung der zu formatierenden Zellen verwenden“.",
            );
          }

          const erste = eigene[0];
          const formel = erste.customFormula;

          // Der klassische Fehler: Sind alle Bezüge vollständig festgehalten,
          // prüfen sämtliche Zellen des Bereichs dieselbe Zelle - dann ist
          // entweder alles gefärbt oder nichts.
          const verwendeteBezuege = formel !== null ? bezuege(formel) : [];
          const alleAbsolut =
            verwendeteBezuege.length > 0 &&
            verwendeteBezuege.every((bezug) => bezug.columnAbsolute && bezug.rowAbsolute);
          if (alleAbsolut) {
            return fail(
              `Die Regel auf „${erste.sheetName}“ benutzt die Formel „${formel}“ – darin ist der Bezug ` +
                "vollständig festgehalten. Dann prüfen alle Zellen dieselbe Zelle, und es ist entweder " +
                "alles gefärbt oder nichts.",
              "Nimm das Dollarzeichen vor der Zeilennummer heraus: aus =$D$2>1000 wird =$D2>1000. " +
                "Die Spalte bleibt fest, die Zeile läuft mit.",
            );
          }

          return pass(
            `Regel mit eigener Formel auf „${erste.sheetName}“` +
              (formel !== null ? `: „${formel}“` : "") +
              `, angewendet auf ${erste.address ?? "einen Bereich"}.` +
              (erste.address !== null && !erste.address.includes(":")
                ? " Der Geltungsbereich umfasst allerdings nur eine Zelle – markiere den Bereich über " +
                  "alle Spalten, damit wirklich die ganze Zeile gefärbt wird."
                : ""),
          );
        },
      },
    },
    {
      id: "farbe-allein",
      title: "Farbe allein reicht nicht",
      explanation: [
        "Rund acht Prozent der Männer und ein halbes Prozent der Frauen unterscheiden " +
          "Rot und Grün schlecht oder gar nicht. Eine Tabelle, in der die " +
          "kritischen Zeilen rot und die guten grün sind, enthält für diese Menschen " +
          "schlicht keine Information – die Farben sehen für sie ähnlich aus.",
        "Die Lösung ist nicht, auf Farbe zu verzichten, sondern sie nie allein " +
          "sprechen zu lassen. Ein Symbolsatz mit unterschiedlichen Formen – Kreis, " +
          "Dreieck, Raute – trägt die Aussage auch ohne Farbe. Ebenso eine zusätzliche " +
          "Spalte mit „kritisch“ oder „in Ordnung“ als Text, die sich nebenbei filtern " +
          "und sortieren lässt.",
        "Wenn es eine Farbskala sein soll, ist Blau nach Orange die verträglichere " +
          "Wahl als Rot nach Grün: Diese beiden bleiben auch bei den häufigsten Formen " +
          "der Farbfehlsichtigkeit unterscheidbar. Excel bietet mehrere Farbskalen an; " +
          "die voreingestellte ist nicht die einzige.",
        "Und ein praktischer Punkt jenseits der Barrierefreiheit: Beim Schwarz-Weiß-" +
          "Druck werden aus Rot und Grün zwei fast gleiche Grautöne. Was nur farblich " +
          "kodiert ist, überlebt den Weg zum Drucker nicht.",
      ],
      keyPoints: [
        "Farbe nie allein sprechen lassen – Form oder Text daneben.",
        "Blau nach Orange ist verträglicher als Rot nach Grün.",
        "Im Schwarz-Weiß-Druck werden Rot und Grün zum selben Grau.",
      ],
    },
  ],
};
